/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { RootState } from "store/store";

/**
 * Props for PrivateRoute component.
 */
interface PrivateRouteProps {
  children: JSX.Element;
  requiredPermissions: string[];
}

/**
 * Protected route component with authentication and permission checks.
 * 
 * Security Features:
 * - Redirects to /login if user is not authenticated
 * - Redirects to /unauthorized if user lacks required permissions
 * - Checks permissions from Redux auth state
 * - Requires at least one matching permission for access
 * - Uses replace navigation to prevent back-button bypass
 * 
 * Used throughout the application to protect routes requiring authentication
 * and specific permissions. Part of the role-based access control (RBAC) system
 * for ISO-27001 compliance.
 * 
 * @param children - Protected page/component to render
 * @param requiredPermissions - Array of permission strings (any match grants access)
 * @returns Protected content if authorized, redirects otherwise
 */
const PrivateRoute = ({ children, requiredPermissions }: PrivateRouteProps) => {
  const { isAuthenticated, permissions } = useSelector((state: RootState) => state.auth);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const hasPermission = requiredPermissions.some((perm) => permissions.includes(perm));
  if (!hasPermission) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

export default PrivateRoute;
